import React from "react";
import { Check, ArrowUpRight } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { LESSONS } from "@/i18n/content";
import { Reveal } from "@/components/site/Reveal";

const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

export default function Lessons({ onSelect }) {
  const { t } = useLang();

  return (
    <section id="lessons" data-testid="lessons-section" className="bg-surface py-16 md:py-24">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <Reveal className="max-w-3xl mb-16">
          <p className="label-mono text-ocean mb-6">{t.lessons.label}</p>
          <h2 className="font-outfit font-semibold text-4xl md:text-6xl tracking-tight text-ink">
            {t.lessons.title}
          </h2>
          <p className="font-manrope text-ink/60 text-base md:text-lg mt-5">{t.lessons.sub}</p>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {LESSONS.map((lesson, i) => (
            <Reveal key={lesson.id} delay={i * 0.1}>
              <div
                data-testid={`lesson-card-${lesson.id}`}
                className="group bg-cream border border-black/10 h-full flex flex-col"
              >
                <div className="overflow-hidden aspect-[16/11] border-b border-black/10">
                  <img
                    src={lesson.image}
                    alt={t.lessons.names[lesson.id]}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <div className="p-7 lg:p-9 flex flex-col flex-1">
                  <div className="flex items-center justify-between mb-4">
                    <span className="label-mono text-ocean">
                      {t.lessons.levelLabels[lesson.level]}
                    </span>
                    <span className="font-mono text-xs text-ink/50">
                      {lesson.duration} · {lesson.group}
                    </span>
                  </div>

                  <h3 className="font-outfit font-semibold text-2xl text-ink leading-tight">
                    {t.lessons.names[lesson.id]}
                  </h3>
                  <p className="font-manrope text-ink/60 mt-3 text-sm">
                    {t.lessons.descs[lesson.id]}
                  </p>

                  <div className="flex items-baseline gap-1 mt-6 mb-6">
                    <span className="font-playfair text-5xl text-navy">€{lesson.price}</span>
                    <span className="font-mono text-xs text-ink/50">{t.lessons.perPerson}</span>
                  </div>

                  <ul className="space-y-2 mb-8">
                    {t.lessons.features.map((f) => (
                      <li key={f} className="flex items-center gap-2 text-sm font-manrope text-ink/70">
                        <Check size={15} className="text-ocean" /> {f}
                      </li>
                    ))}
                  </ul>

                  <button
                    data-testid={`lesson-book-${lesson.id}`}
                    onClick={() => {
                      onSelect?.(lesson.id);
                      scrollTo("booking");
                    }}
                    className="mt-auto w-full flex items-center justify-between bg-navy text-white label-mono px-6 py-4 hover:bg-ocean transition-colors duration-300"
                  >
                    {t.lessons.book}
                    <ArrowUpRight size={18} />
                  </button>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
